/**
 * core.module.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { ModuleWithProviders, NgModule, Provider } from "@angular/core";

// ======== MESSAGE SERVICES ========
/**
 * Application services that manage or are dependent upon the flow of messages
 * between the server and client.
 */
import { AlgorithmStepService }
from "../algorithms/management/algorithm-step.service";
import { AlgorithmService }
  from "../algorithms/management/algorithm.service";
import { IntakeStepService }
  from "../algorithms/management/intake-step.service";
import { ReportStepService }
  from "../algorithms/management/report-step.service";
import { UserService } from "../auth/identity/user.service";
import { LoginService } from "../auth/login.service";
import { DebugService } from "../debug/debug.service";
import { GraphService } from "../graphs/graph.service";
import { CollaborationService } from "../intake-form/collaboration.service";
import { CommentService } from "../intake-form/comment.service";
import { SearchService } from "../search/search.service";
import { MessagingService } from "../server/messaging.service";
import { ProtocolVersionService } from "../server/protocol-version.service";
import { WatchlistService } from "../watchlist/watchlist.service";

const MESSAGE_SERVICES : any[] = [
  AlgorithmService,
  AlgorithmStepService,
  CollaborationService,
  CommentService,
  DebugService,
  GraphService,
  IntakeStepService,
  LoginService,
  MessagingService,
  ProtocolVersionService,
  ReportStepService,
  SearchService,
  UserService,
  WatchlistService
];

// ======== OTHER SERVICES ========
/**
 * Application services that provide message-independent features.
 */
import { Title } from "@angular/platform-browser";
import { STEP_TRANSFORMER_LIST, TransformerService }
  from "../algorithms/management/pipeline/transformer.service";
import { AlertService } from "../common/alert.service";
import { BrandingService } from "../common/branding.service";
import { ModalService } from "../common/modal.service";
import { PrintService } from "../common/print.service";
import { ConfigurationService } from "../configuration/configuration.service";
import { SettingService } from "../configuration/setting.service";
import { NavigationHistoryService } from "../debug/navigation-history.service";
import { SupportService } from "../debug/support.service";
import { BadgeService } from "../intake-form/badge.service";
import { SubjectService } from "../subjects/subject.service";
import { SoundsService } from "../support/APIs/sounds.service";
import { StorageService } from "../support/APIs/storage.service";
import { UseModalsForBrowserAlerts } from "../support/utilities/modals";

// Special inclusions with side-effects and other weirdness
import { CONFIG_INITIALIZER } from "../configuration/configuration.service";
import { CUSTOM_ERROR_HANDLER } from "../debug/global-error-handler";
import { LAUNCH_DEPENDENCY_PROVIDER } from "../launch-dependencies";

const GENERIC_SERVICES : any[] = [
  AlertService,
  BadgeService,
  BrandingService,
  ConfigurationService,
  CUSTOM_ERROR_HANDLER,
  LAUNCH_DEPENDENCY_PROVIDER,
  ModalService,
  NavigationHistoryService,
  PrintService,
  SettingService,
  SoundsService,
  STEP_TRANSFORMER_LIST,
  StorageService,
  SubjectService,
  SupportService,
  TransformerService,
  Title,
  UseModalsForBrowserAlerts
];

// ======== PAGE ELEMENTS ========
/**
 * These are subcomponents of the UI, lower than the route level. They will
 * generally be @Components with selectors, though a few other types might
 * get included here (for example, some directives, like DateValueAccessor).
 */

// import { AdvancedColumnSelectionModalComponent }
// 	from "../aggregation/advanced-column-selection-modal.component"
import { ChangePasswordModalComponent }
  from "../auth/change-password-modal.component";
import { ChangeSecurePromptsModalComponent }
  from "../auth/change-secure-prompts-modal.component";
import { UserProfileComponent } from "../auth/identity/user-profile.component";
import { PasswordStrengthAnalysisComponent }
  from "../auth/password-strength-analysis.component";
import { AlertListComponent } from "../common/alert-list.component";
import { AnnouncementComponent } from "../common/announcement.component";
import { ChangelogModalComponent }
  from "../common/changelog-modal.component";
import { ControlTableModalComponent }
  from "../common/data/control-table-modal.component";
import { ShortcutHelpModalComponent } from "../common/shortcut-help-modal.component";
import { IframeModalComponent }
  from "../common/iframe-modal-dialog.component";
import { NavigationBarComponent } from "../common/navigation-bar.component";
import { ExplanationModalComponent }
  from "../debug/explanation-modal.component";
import { JsonModalComponent }
  from "../debug/json-modal.component";
import { MockReceiverModalComponent }
  from "../debug/mock-receiver-modal.component";
import { RawSenderModalComponent } from "../debug/raw-sender-modal.component";
import { BadgeComponent } from "../intake-form/badge.component";
import { ChatPanelComponent } from "../intake-form/chat-panel.component";
import { CollaboratorPanelComponent }
  from "../intake-form/collaborator-panel.component";
import { ExportModalComponent }
  from "../intake-form/export-modal.component";
import { IntakeStepListComponent }
  from "../intake-form/intake-step-list.component";
import { JumpToStepModalComponent }
  from "../intake-form/jump-to-step-modal.component";
import { QuickLinkComponent }
  from "../intake-form/quick-link.component";
import { ReferralModalComponent }
  from "../intake-form/referral-modal.component";
import { ReportDetailsModalComponent }
  from "../intake-form/report-details-modal.component";
import { ReportPanelComponent }
  from "../intake-form/report-panel.component";
import { RelationGraphModalComponent }
  from "../subjects/relation-graph-modal.component";
import { RelationshipPanelComponent }
  from "../subjects/relationship-panel.component";
import { SelectGroupsModalComponent }
  from "../subjects/select-groups-modal.component";
import { SubjectSelectorModalComponent }
  from "../subjects/subject-selector-modal.component";
import { AddReminderModalComponent }
  from "../watchlist/add-reminder-modal.component";
import { CreateSharedListModalComponent }
  from "../watchlist/create-shared-list-modal.component";
import { ReminderListComponent }
  from "../watchlist/reminder-list.component";
import { WatchButtonComponent } from "../watchlist/watch-button.component";
import { WatchlistComponent } from "../watchlist/watchlist.component";
import { WorklistConfigModalComponent }
  from "../server/messages/watchlists/worklist-config-modal.component";

// TODO: modularize search
import { SEARCH_COMPONENTS } from "../search/subject-search.component";

/**
 * Modals are listed separately so we can easily add them to the module's
 * entryComponents as well (otherwise the ng compiler won't see them as linked)
 */
const MODALS : any[] = [
  AddReminderModalComponent,
  // AdvancedColumnSelectionModalComponent,
  ChangelogModalComponent,
  ChangePasswordModalComponent,
  ChangeSecurePromptsModalComponent,
  ControlTableModalComponent,
  CreateSharedListModalComponent,
  ExplanationModalComponent,
  ExportModalComponent,
  ShortcutHelpModalComponent,
  IframeModalComponent,
  JsonModalComponent,
  JumpToStepModalComponent,
  MockReceiverModalComponent,
  QuickLinkComponent,
  RawSenderModalComponent,
  ReferralModalComponent,
  RelationGraphModalComponent,
  ReportDetailsModalComponent,
  SelectGroupsModalComponent,
  SubjectSelectorModalComponent,
  WorklistConfigModalComponent
];

const PAGE_ELEMENTS : any[] = [
  // top-level components
  AlertListComponent,
  AnnouncementComponent,
  NavigationBarComponent,

  // subject/algorithm/intake components
  IntakeStepListComponent,
  RelationshipPanelComponent,
  ReportPanelComponent,
  BadgeComponent,
  ChatPanelComponent,
  CollaboratorPanelComponent,
  WatchButtonComponent,

  // admin/user/notification components
  WatchlistComponent,
  ReminderListComponent,
  UserProfileComponent,
  PasswordStrengthAnalysisComponent
];

// ======== AUTH / ROUTING ========
/*
 * Services, components (generally pages), and the configured module necessary
 * to handle routing and authentication.
 */
import { RouterModule } from "@angular/router";
import { AUTH_PROVIDERS } from "../auth/auth.service";
import { APP_ROUTING_COMPONENTS, APPLICATION_GUARDS, ROOT_ROUTING }
  from "../routing/app.routing";

// modules
import { SharedModule } from "../shared/shared.module";

@NgModule({
    imports: [
        SharedModule,
        ROOT_ROUTING
    ],
    declarations: [
        MODALS,
        PAGE_ELEMENTS,
        SEARCH_COMPONENTS,
        APP_ROUTING_COMPONENTS
    ],
    entryComponents: [ MODALS ],
    providers: [
        CUSTOM_ERROR_HANDLER,
        MESSAGE_SERVICES,
        GENERIC_SERVICES,
        CONFIG_INITIALIZER,
        LAUNCH_DEPENDENCY_PROVIDER,
        APPLICATION_GUARDS,
        AUTH_PROVIDERS
    ],
    exports: [ MODALS, PAGE_ELEMENTS, SEARCH_COMPONENTS, RouterModule ],
 })
export class CoreModule {}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/core/core.module.ts